import { Destination } from "Constant/destination";

import { GOOGLE_ADS_CONVERSION_TYPES } from "components/organisms/Destinations/GoogleAdsConversion/GoogleAdsConversion.helpers";

/** Pixel-only destinations created straight from the "Pixel id" field. */
export const PIXEL_ONLY_CREATE_TYPES = [
    Destination.ttpixel,
    Destination.fbpixel,
    Destination.alpixel,
    Destination.scpixel,
    Destination.rdpixel,
];

/** Pixel + token destinations created without client-side validation. */
export const PIXEL_TOKEN_CREATE_TYPES = [
    Destination.sccapi,
    Destination.ttcapi,
    Destination.ttoffline,
];

export const TIKTOK_TYPES = [
    Destination.ttpixel,
    Destination.ttcapi,
    Destination.ttoffline,
];

/** TikTok types whose Save depends on the selected advertiser pixels / event sets. */
export const TIKTOK_SELECTION_TYPES = [
    Destination.ttpixel,
    Destination.ttoffline,
    Destination.ttcapi,
];

export const PINTEREST_TYPES = [Destination.pncapi, Destination.pnpixel];

export const TWITTER_TYPES = [Destination.twcapi, Destination.twpixel];

export const GOOGLE_ENHANCED_TYPES = [Destination.ggads, Destination.ggads_web];

/**
 * Types whose configure-step submit is registered by the form (`"form"`) or
 * the mapping step (`"mapping"`, which wins). While nothing is registered the
 * footer shows Back and Cancel only. Every other type keeps the host
 * "Create destination" button.
 */
export const FOOTER_ACTION_DESTINATION_TYPES = [
    Destination.ggbq,
    ...GOOGLE_ENHANCED_TYPES,
    Destination.licapi,
    Destination.msoffline,
    Destination.ymwc,
    ...GOOGLE_ADS_CONVERSION_TYPES,
    Destination.oaicapi,
];

/** Types that need the Facebook external business id (FBE). */
export const FACEBOOK_BUSINESS_TYPES = [Destination.fbcapi, Destination.fbca];

/** Save starts disabled when one of these is selected (Google Ads conversion types too). */
export const SAVE_DISABLED_ON_SELECT_TYPES = [
    Destination.ggads4,
    Destination.twpixel,
    Destination.twcapi,
    Destination.pncapi,
    Destination.pnpixel,
    Destination.imcapi,
    Destination.ttcapi,
    Destination.ttoffline,
    Destination.ggbq,
    Destination.licapi,
    Destination.msoffline,
    Destination.ymwc,
    ...GOOGLE_ENHANCED_TYPES,
];

/** Types that never render the generic "Pixel id" field wrapper. */
export const PIXEL_FIELD_EXCLUDED_TYPES = [
    ...GOOGLE_ENHANCED_TYPES,
    Destination.ggads4,
    ...TWITTER_TYPES,
    Destination.pncapi,
    Destination.fbca,
    Destination.ttca,
    Destination.imcapi,
    Destination.sstask,
    Destination.ggbq,
    Destination.licapi,
    Destination.msoffline,
    Destination.ymwc,
];

/** Types that never render the generic "Access Token" field. */
export const TOKEN_FIELD_EXCLUDED_TYPES = [
    Destination.ttpixel,
    ...GOOGLE_ENHANCED_TYPES,
    Destination.ggads4,
    Destination.fbpixel,
    Destination.alpixel,
    Destination.scpixel,
    Destination.pnpixel,
    Destination.pncapi,
    Destination.rdpixel,
    Destination.fbca,
    Destination.ttca,
    Destination.imcapi,
    Destination.sstask,
    Destination.ggbq,
    Destination.licapi,
    Destination.msoffline,
    Destination.ymwc,
    Destination.oaipixel,
    Destination.oaicapi,
    ...TWITTER_TYPES,
];

/** Types that show the "enable pixel tracking at the browser level" switch. */
export const CREATE_PIXEL_TOGGLE_TYPES = [
    Destination.pncapi,
    Destination.fbcapi,
    Destination.sccapi,
    Destination.ttcapi,
    Destination.ttoffline,
    Destination.twcapi,
    Destination.rdcapi,
];

export const HELP_LINKS = Object.freeze({
    meta: {
        pixelId:
            "https://signalsight.io/docs/facebook-capi-setup.html#fb-pixel-ID",
        token: "https://signalsight.io/docs/facebook-capi-setup.html#fb-generate-access",
    },
    tiktok: {
        pixelId:
            "https://signalsight.io/docs/tiktok-events-api-setup.html#tiktok-pixel-ID",
        token: "https://signalsight.io/docs/tiktok-events-api-setup.html#tiktok-generate-access",
    },
    snapchat: {
        pixelId:
            "https://businesshelp.snapchat.com/s/article/snap-pixel-about?language=en_US",
        token: "https://businesshelp.snapchat.com/s/article/api-apply?language=en_US",
    },
    applovin: { pixelId: null, token: null },
});

/** Which help popover the "Pixel id" / "Access Token" fields show per type. */
export const HELP_LINKS_BY_DESTINATION = Object.freeze({
    [Destination.fbpixel]: HELP_LINKS.meta,
    [Destination.fbcapi]: HELP_LINKS.meta,
    [Destination.fboffline]: HELP_LINKS.meta,
    [Destination.pnpixel]: HELP_LINKS.meta,
    [Destination.ttpixel]: HELP_LINKS.tiktok,
    [Destination.ttcapi]: HELP_LINKS.tiktok,
    [Destination.ttoffline]: HELP_LINKS.tiktok,
    [Destination.scpixel]: HELP_LINKS.snapchat,
    [Destination.sccapi]: HELP_LINKS.snapchat,
    [Destination.alpixel]: HELP_LINKS.applovin,
});

export const EMPTY_PINTEREST_FORM = Object.freeze({
    accountId: "",
    accessToken: "",
    pinterestTagId: "",
});

export const FACEBOOK_CAPI_SCOPE =
    "email,manage_business_extension,ads_management,pages_read_engagement,ads_read,business_management";

export const UNAVAILABLE_DESTINATION_TOOLTIP =
    "Pixel destinations only accept Website JS-based web events. CRM, offline, or messaging events cannot be sent to these destinations.";
